/**
 * Zod schemas for event data validation.
 * Each event type has a corresponding schema for its eventData field.
 */

import { z } from 'zod';

// =============================================================================
// Call Event Schemas
// =============================================================================

export const callStartedDataSchema = z.object({}).optional();

export const callCompletedDataSchema = z.object({
  duration: z.number().optional(),
  outcome: z.string().optional(),
  recordingUrl: z.string().optional(),
});

export const callTransferredDataSchema = z.object({
  reason: z.string().optional(),
  transferredTo: z.string().optional(),
});

// =============================================================================
// SMS Event Schemas
// =============================================================================

export const smsSentDataSchema = z.object({
  to: z.string(),
  message: z.string(),
  twilioSid: z.string().optional(),
});

export const smsReceivedDataSchema = z.object({
  from: z.string(),
  message: z.string(),
});

export const smsDeliveredDataSchema = z.object({
  twilioSid: z.string().optional(),
  deliveredAt: z.string().optional(),
});

export const smsFailedDataSchema = z.object({
  error: z.string().optional(),
  twilioSid: z.string().optional(),
});

// =============================================================================
// Appointment Event Schemas
// =============================================================================

export const appointmentBookedDataSchema = z.object({
  appointmentId: z.string().optional(),
  date: z.string(),
  time: z.string(),
  appointmentType: z.string(),
  provider: z.object({
    id: z.string(),
    name: z.string(),
  }),
  location: z.object({
    id: z.string(),
    name: z.string(),
  }),
});

export const appointmentCancelledDataSchema = z.object({
  appointmentId: z.string().optional(),
  reason: z.string().optional(),
});

export const appointmentRescheduledDataSchema = z.object({
  appointmentId: z.string().optional(),
  oldDate: z.string().optional(),
  oldTime: z.string().optional(),
  newDate: z.string(),
  newTime: z.string(),
});

// =============================================================================
// EHR Event Schemas
// =============================================================================

export const ehrSyncedDataSchema = z.object({
  system: z.string(),
  referenceId: z.string(),
});

export const ehrFailedDataSchema = z.object({
  system: z.string(),
  error: z.string(),
});

// =============================================================================
// Task Event Schemas
// =============================================================================

export const taskCreatedDataSchema = z.object({
  taskId: z.string().optional(),
  title: z.string(),
});

export const taskCompletedDataSchema = z.object({
  taskId: z.string().optional(),
});

// =============================================================================
// Other Outcome Event Schemas
// =============================================================================

export const refillRequestedDataSchema = z.object({
  medication: z.string(),
  pharmacy: z.string().optional(),
  pharmacyPhone: z.string().optional(),
});

export const recallScheduledDataSchema = z.object({
  recallType: z.string(),
  scheduledDate: z.string().optional(),
});

export const collectionRecordedDataSchema = z.object({
  amount: z.number(),
  paymentMethod: z.string().optional(),
});

export const noteAddedDataSchema = z.object({
  note: z.string(),
  addedBy: z.string().optional(),
});

// =============================================================================
// Schema Registry
// =============================================================================

/**
 * Registry of event data schemas by event type.
 */
export const EVENT_DATA_SCHEMAS: Record<string, z.ZodSchema> = {
  // Call events
  'call:started': callStartedDataSchema,
  'call:completed': callCompletedDataSchema,
  'call:transferred': callTransferredDataSchema,

  // SMS events
  'sms:sent': smsSentDataSchema,
  'sms:received': smsReceivedDataSchema,
  'sms:delivered': smsDeliveredDataSchema,
  'sms:failed': smsFailedDataSchema,

  // Appointment events
  'appointment:booked': appointmentBookedDataSchema,
  'appointment:cancelled': appointmentCancelledDataSchema,
  'appointment:rescheduled': appointmentRescheduledDataSchema,

  // EHR events
  'ehr:synced': ehrSyncedDataSchema,
  'ehr:failed': ehrFailedDataSchema,

  // Task events
  'task:created': taskCreatedDataSchema,
  'task:completed': taskCompletedDataSchema,

  // Other outcome events
  'refill:requested': refillRequestedDataSchema,
  'recall:scheduled': recallScheduledDataSchema,
  'collection:recorded': collectionRecordedDataSchema,
  'note:added': noteAddedDataSchema,
};

/**
 * Validate event data against its schema.
 * Returns true if valid or if no schema exists (unknown event types are allowed).
 */
export function validateEventData(eventType: string, data: unknown): boolean {
  const schema = EVENT_DATA_SCHEMAS[eventType];
  if (!schema) return true; // Unknown types pass (extensible)
  return schema.safeParse(data).success;
}

/**
 * Get the schema for an event type.
 */
export function getEventDataSchema(eventType: string): z.ZodSchema | undefined {
  return EVENT_DATA_SCHEMAS[eventType];
}

// =============================================================================
// Type Exports
// =============================================================================

export type CallStartedData = z.infer<typeof callStartedDataSchema>;
export type CallCompletedData = z.infer<typeof callCompletedDataSchema>;
export type CallTransferredData = z.infer<typeof callTransferredDataSchema>;

export type SmsSentData = z.infer<typeof smsSentDataSchema>;
export type SmsReceivedData = z.infer<typeof smsReceivedDataSchema>;
export type SmsDeliveredData = z.infer<typeof smsDeliveredDataSchema>;
export type SmsFailedData = z.infer<typeof smsFailedDataSchema>;

export type AppointmentBookedData = z.infer<typeof appointmentBookedDataSchema>;
export type AppointmentCancelledData = z.infer<typeof appointmentCancelledDataSchema>;
export type AppointmentRescheduledData = z.infer<typeof appointmentRescheduledDataSchema>;

export type EhrSyncedData = z.infer<typeof ehrSyncedDataSchema>;
export type EhrFailedData = z.infer<typeof ehrFailedDataSchema>;

export type TaskCreatedData = z.infer<typeof taskCreatedDataSchema>;
export type TaskCompletedData = z.infer<typeof taskCompletedDataSchema>;

export type RefillRequestedData = z.infer<typeof refillRequestedDataSchema>;
export type RecallScheduledData = z.infer<typeof recallScheduledDataSchema>;
export type CollectionRecordedData = z.infer<typeof collectionRecordedDataSchema>;
export type NoteAddedData = z.infer<typeof noteAddedDataSchema>;
